import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { addDays, differenceInDays } from "date-fns";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getContentClient } from "sj-ui-toolkit/src/client/svcContent";
import { getChoresClient } from "sj-ui-toolkit/src/client/svcClients";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import TrainingAssignmentSettings, {
  type TrainingAssignmentSettingsValues,
} from "@/components/pla/TrainingAssignmentSettings";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";

type ChoreAssignment = Awaited<ReturnType<ReturnType<typeof getChoresClient>["getChore"]>>["data"];

const REMINDER_TO_PREFERENCE: Record<string, "None" | "Weekly" | "Monthly" | "Quarterly"> = {
  none: "None",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
};

const PREFERENCE_TO_REMINDER: Record<string, string> = {
  None: "none",
  Weekly: "weekly",
  Monthly: "monthly",
  Quarterly: "quarterly",
};

const endDateFromDates = (
  dates: { availableAt?: string; dueAfter?: { days?: number } } | undefined
) => {
  if (!dates?.availableAt || !dates.dueAfter) return null;

  const days = Number(dates.dueAfter.days);
  if (!Number.isFinite(days) || days <= 0) return null;

  return addDays(new Date(dates.availableAt), days).toISOString();
};

export default function PersonalizedLessonAssignmentEdit() {
  const { uuid = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { features, isLoading: featuresLoading, currentTenantName } = useAspenFeatures();

  useBreadcrumb({
    label: "Edit Personalized Lesson Assignment",
    route:
      AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.routes.edit.route.replace(
        ":uuid",
        uuid
      ),
    display: "page",
    resetTrail: false,
  });

  const [assignment, setAssignment] = useState<ChoreAssignment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<TrainingAssignmentSettingsValues>({
    startDate: null,
    endDate: null,
    reminders: "none",
    name: "",
    description: "",
  });
  const [lessons, setLessons] = useState<
    { lessonKey: string; lessonName: string; lessonDescription?: string }[]
  >([]);

  const learnerName = (location.state as { learnerName?: string } | null)?.learnerName || "";

  useEffect(() => {
    if (!currentTenantName || !features.adapt) {
      setIsLoading(false);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const assignmentResponse = await getChoresClient().getChore(uuid);
        if (assignmentResponse.error) throw assignmentResponse.error;

        const currentAssignment = assignmentResponse.data;
        if (!currentAssignment) {
          showToast({ severity: "error", detail: "Assignment not found." });
          navigate(AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.route);
          return;
        }

        setAssignment(currentAssignment);

        const firstItem = currentAssignment.items?.[0];
        const dates = firstItem?.dates as
          | { availableAt?: string; dueAfter?: { days?: number } }
          | undefined;
        const notify = firstItem?.notify;
        setSettings({
          startDate: dates?.availableAt || null,
          endDate: endDateFromDates(dates),
          reminders: PREFERENCE_TO_REMINDER[notify?.sendReminders || "None"] || "none",
          name: "",
          description: "",
        });

        const currentKeys = (currentAssignment.items || [])
          .map(item =>
            item.content && "lesson" in item.content ? item.content.lesson?.lessonKey : undefined
          )
          .filter((lessonKey): lessonKey is string => !!lessonKey);

        if (currentKeys.length > 0) {
          try {
            const details = await getContentClient().getLessonsPublic(
              currentKeys,
              currentTenantName
            );
            const lessonsByKey = new Map(
              details.map(detail => [
                detail.lessonKey,
                {
                  lessonKey: detail.lessonKey || "",
                  lessonName: detail.lessonName || detail.lessonKey || "",
                  lessonDescription: detail.lessonDescription || "",
                },
              ])
            );
            setLessons(
              currentKeys.map(lessonKey => ({
                lessonKey,
                lessonName: lessonsByKey.get(lessonKey)?.lessonName || lessonKey,
                lessonDescription: lessonsByKey.get(lessonKey)?.lessonDescription || "",
              }))
            );
          } catch (error) {
            console.error("Error loading lesson details:", error);
            setLessons(currentKeys.map(lessonKey => ({ lessonKey, lessonName: lessonKey })));
          }
        } else {
          setLessons([]);
        }
      } catch (error) {
        console.error("Error loading assignment:", error);
        showToast({
          severity: "error",
          detail: "Unable to load the assignment. Please try again.",
        });
        navigate(AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.route);
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [currentTenantName, features.adapt, navigate, showToast, uuid]);

  const canSubmit = useMemo(
    () => !!settings.startDate && !!settings.endDate,
    [settings.endDate, settings.startDate]
  );

  const buildPayload = () => {
    const startDate = new Date(settings.startDate || "");
    const endDate = new Date(settings.endDate || "");
    const days = Math.max(1, differenceInDays(endDate, startDate));

    return {
      tenant: currentTenantName,
      dates: {
        availableAt: startDate.toISOString(),
        dueAfter: { days },
      },
      notify: {
        notifyWhenAvailable: true,
        sendReminders: REMINDER_TO_PREFERENCE[settings.reminders] || "None",
      },
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await getChoresClient().modifyLessonAssignment(uuid, buildPayload());
      if (error) throw error;

      showToast({
        severity: "success",
        detail: "Personalized lesson assignment updated.",
      });
      navigate(AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.route);
    } catch (error) {
      console.error("Error updating assignment:", error);
      showToast({
        severity: "error",
        detail: "Unable to update the assignment. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (featuresLoading || isLoading) {
    return <Skeleton width="100%" height="20rem" />;
  }

  if (!features.adapt || !assignment) {
    return null;
  }

  return (
    <div>
      <h1 className="mb-2">Edit Personalized Lesson Assignment</h1>
      {learnerName && <p className="mb-6 body-md text-secondary">for {learnerName}</p>}

      <section className="mb-8">
        <h2 className="mb-3">Schedule &amp; notifications</h2>
        <TrainingAssignmentSettings
          settings={settings}
          onSettingsChange={setSettings}
          hideDaily
          hideNameDescription
        />
      </section>

      <section className="mb-8">
        <h2 className="mb-3">Lessons</h2>
        <p className="mb-3 body-sm text-secondary">
          {lessons.length} {lessons.length === 1 ? "lesson" : "lessons"} assigned. Lessons cannot be
          changed after a PLA is approved.
        </p>
        <DataTable
          value={lessons}
          dataKey="lessonKey"
          sortMode="single"
          className="p-datatable-sm"
          aria-label="Lessons in this assignment"
          tableClassName="dt-responsive-text"
          emptyMessage="No lessons in this assignment."
        >
          <Column field="lessonName" header="Lesson Name" sortable />
          <Column field="lessonDescription" header="Description" />
        </DataTable>
      </section>

      <div className="flex justify-end gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          onClick={() =>
            navigate(AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.route)
          }
          disabled={isSaving}
        />
        <Button
          label="Save"
          onClick={() => void handleSave()}
          loading={isSaving}
          disabled={!canSubmit}
        />
      </div>
    </div>
  );
}
